import React from "react";
import PropTypes from "prop-types";
import Mousetrap from "mousetrap";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import classNames from "classnames/bind";
import { canUseDOM } from "fbjs/lib/ExecutionEnvironment";
import { Container, Row, Col, Button } from "reactstrap";
import Tock from "tocktimer";
import { isDesktopApp, isProductionEnvironment, mailTo, isGamedistributionApp } from "~/utils/misc";
import { getCookie, saveCookie } from "~/utils/cookie";
import WonModalComponent from "~/components/WonModalComponent";
import { countUp } from "~/utils/board";
import {
  homeButtonElement,
  previewButtonElement,
  edgesButtonElement,
  shuffleButtonElement,
  backButtonElement,
  settingsButtonElement,
  helpButtonElement,
  scoreContainerElement
} from "./helpers/elements";
import {
  settingsModalElement,
  onSettingTap
} from "./helpers/settingsModalElement";
import helpModalElement from "./helpers/helpModalElement";
import LinkComponent from "~/components/LinkComponent";

import styles from "./styles.scss";

class MenuComponent extends React.Component {
  static propTypes = {
    shouldShowBackButton: PropTypes.bool.isRequired,
    isPuzzlePage: PropTypes.bool.isRequired,
    isGameActive: PropTypes.bool.isRequired,
    isGameAnimating: PropTypes.bool.isRequired,
    startTime: PropTypes.instanceOf(Date).isRequired,
    isEdgesActive: PropTypes.bool.isRequired,
    isPreviewActive: PropTypes.bool.isRequired,
    startNewGame: PropTypes.func.isRequired,
    toggleEdges: PropTypes.func,
    togglePreview: PropTypes.func,
    onRef: PropTypes.func.isRequired
  };

  constructor(props) {
    // Set props
    super(props);

    // Set initial state
    this.state = {
      breakpoint: "md",
      timer: null
    };

    // Bind element functions
    this.homeButtonElement = homeButtonElement.bind(this);
    this.previewButtonElement = previewButtonElement.bind(this);
    this.edgesButtonElement = edgesButtonElement.bind(this);
    this.shuffleButtonElement = shuffleButtonElement.bind(this);
    this.backButtonElement = backButtonElement.bind(this);
    this.settingsButtonElement = settingsButtonElement.bind(this);
    this.helpButtonElement = helpButtonElement.bind(this);
    this.scoreContainerElement = scoreContainerElement.bind(this);

    // Bind settings modal element functions
    this.settingsModalElement = settingsModalElement.bind(this);
    this.onSettingTap = onSettingTap.bind(this);

    // Bind modal element functions
    this.helpModalElement = helpModalElement.bind(this);

    // Bind board functions
    this.countUp = countUp.bind(this);
  }

  //-----------------------------------------------------------------------
  // Lifecycle
  //-----------------------------------------------------------------------
  componentWillMount() {
    this.props.onRef(this);
  }

  componentDidMount() {
    // Listen for Electron action taps
    if (isDesktopApp() && is.existy(window.electron)) {
      // Listen to electron menu
      window.electron.onMessageFromRendererToWebview(params => {
        // Open new mail
        if (is.equal(params.action, "email_developer")) {
          mailTo()
        }

        // On settings clicked
        if (is.equal(params.action, "settings_start_on_login")) {
          this.onSettingTap(
            "macSettingsStartAtLogin",
            is.truthy(params.checked)
          );
        } else if (is.equal(params.action, "settings_sound")) {
          this.onSettingTap(
            "settingsSound",
            is.truthy(params.checked)
          );
        } else if (is.equal(params.action, "settings_show_time")) {
          this.onSettingTap(
            "settingsShowTime",
            is.truthy(params.checked)
          );
        }

        // On help clicked
        if (is.equal(params.action, "help_open_developer_tools")) {
          window.electron.sendMessageFromWebviewToRenderer({
            action: "open_developer_tools"
          });
        } else if (is.equal(params.action, "info_tapped")) {
          this.handleHelpTap()
        } else if (is.equal(params.action, "settings_tapped")) {
          this.handleSettingsTap()
        } else if (is.equal(params.action, "jigsaw_settings_tapped")) {
          if (params.setting == "edges") {
            this.handleEdgesTap()
          } else if (params.setting == "preview") {
            this.handlePreviewTap()
          } else if (params.setting == "shuffle") {
            this.handleShuffleTap()
          }
        }
      });
    }

    // Update the UI
    this.handleWindowResize();

    // Set breakpoint
    this.state.breakpoint = Breakpoint.current();

    // Create listeners
    emitter.on("startNewGameComplete", this.handleStartNewGameComplete);
    emitter.on("windowResizeEvent", this.handleWindowResize);
    emitter.on("gameWasWon", this.handleGameWasWon);
    emitter.on("toggleEdgesComplete", this.handleToggleEdges);
    emitter.on("togglePreviewComplete", this.handleTogglePreview);
    emitter.on("modalDidShow", this.pauseTimer);
    emitter.on("modalDidHide", this.continueTimer);

    window.addEventListener('blur', this.pauseTimer, false);
    window.addEventListener('focus', this.continueTimer, false);
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  //-----------------------------------------------------------------------
  // Handlers
  //-----------------------------------------------------------------------
  handleWindowResize = () => {
    // Re-render UI
    if (is.not.equal(this.state.breakpoint, Breakpoint.current())) {
      // Set state
      this.state.breakpoint = Breakpoint.current();

      // Force update
      this.forceUpdate();
    }
  };

  handleStartNewGameComplete = () => {
    this.forceUpdate()

    // Start game timer
    this.startGameTimer();

    if (isDesktopApp() && is.existy(window.electron)) {
      // Listen to electron menu
      window.electron.sendMessageFromWebviewToRenderer({
        action: "game_started"
      });
      window.electron.sendMessageFromWebviewToRenderer({
        action: "jigsaw_settings_changed",
        setting: "preview",
        value: this.props.isPreviewActive
      });
    }

    if (getCookie("gamesPlayedAmount", 0) <= 1) {
      let notification = "👆Click and drag a piece to move it."
      if (bowser.mobile && bowser.tablet) {
        notification += "\n🔄Tap a piece to rotate it."
      } else {
        notification += "\n🔄Right click a piece to rotate it."
      }
      $.notify(notification, { delay: 9600 });
    }
  };

  settingsDidChange = () => {
    // Update HTML
    this.forceUpdate();
  };

  handleToggleEdges = () => {
    this.forceUpdate()
  };

  handleTogglePreview = () => {
    this.forceUpdate()
  };

  handleBackTap = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating)) {
      return;
    }

    window.history.back();
  };

  handleHomeTap = (event) => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating)) {
      return;
    }

    if (isGamedistributionApp() || isDesktopApp()) {
      event.preventDefault();
      window.location.href = `${window.location.origin}/${window.location.search}`;
    }
  };

  handleEdgesTap = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating) || !this.props.isGameActive) {
      return;
    }

    this.props.toggleEdges()

    if (isDesktopApp() && is.existy(window.electron)) {
      // Listen to electron menu
      window.electron.sendMessageFromWebviewToRenderer({
        action: "jigsaw_settings_changed",
        setting: "edges",
        value: this.props.isEdgesActive
      });
    }
  };

  handlePreviewTap = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating) || ! this.props.isGameActive) {
      return;
    }

    saveCookie("settingsShowPreview", !getCookie("settingsShowPreview", true))

    this.props.togglePreview()

    if (isDesktopApp() && is.existy(window.electron)) {
      // Listen to electron menu
      window.electron.sendMessageFromWebviewToRenderer({
        action: "jigsaw_settings_changed",
        setting: "preview",
        value: this.props.isPreviewActive
      });
    }
  };

  handleShuffleTap = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating) || !this.props.isGameActive) {
      return;
    }

    // Emit event
    emitter.emit("shuffleTapped");
  };

  handleSettingsTap = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating)) {
      return;
    }

    // Open settings modal
    this.settingsModal.showModal();
  };

  handleHelpTap = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating)) {
      return;
    }

    // Open help modal
    this.helpModal.showModal();
  };

  handleGameWasWon = () => {
    // Show confetti
    _.delay(() => {
      $.confetti.start();
      $("#confettiCanvas").insertBefore(".won_modal_component");
    }, 320);

    this.wonModalComponent.setState({
      playtime: parseInt((new Date() - this.props.startTime) / 1000)
    })

    // Show modal
    this.wonModalComponent.showModal();

    // Remove confetti and modal on click
    $("#confettiCanvas").on("click", () => {
      // Stop confetti
      $.confetti.stop();

      // Hide modal
      this.wonModalComponent.hideModal();
    });

    // Stop game timer
    this.stopGameTimer();

    // Set time
    $(".score_container .time").text(
      `Time: ${this.countUp(this.props.startTime)}`
    );
  };

  macSettingsStartAtLogin = (value) => {
    // Save setting
    saveCookie("macSettingsStartAtLogin", value);

    // Set Electron menu
    if (isDesktopApp() && is.existy(window.electron)) {
      // Set Electron menu
      window.electron.setElectronMenu({
        settings_start_on_login: {
          checked: is.truthy(getCookie("macSettingsStartAtLogin", false)),
          enabled: true
        }
      });

      // Set start at login if needed
      window.electron.setStartAtLogin(value);
    }

    this.forceUpdate()
  }

  pauseTimer = () => {
    this.pauseTime = new Date();

    this.stopGameTimer()
  };

  continueTimer = params => {
    if (!!this.pauseTime && !!this.state.timer) {
      this.props.subtractFromTime({ duration: (new Date()).valueOf() - this.pauseTime.valueOf() });

      this.state.timer.start();

      this.pauseTime == null;
    }
  };

  //-----------------------------------------------------------------------
  // Helpers
  //-----------------------------------------------------------------------
  stopGameTimer = () => {
    // Stop former timer
    if (is.existy(this.state.timer)) {
      this.state.timer.stop();
    }
  };

  startGameTimer = () => {
    // Stop former timer
    if (is.existy(this.state.timer)) {
      this.state.timer.stop();
    }

    // Create timer
    this.state.timer = new Tock({
      interval: 1000,
      callback: () => {
        // Set time
        $(".score_container .time").text(
          `Time: ${this.countUp(this.props.startTime)}`
        );

        if (isDesktopApp() && is.existy(window.electron)) {
          // Listen to electron menu
          window.electron.sendMessageFromWebviewToRenderer({
            action: "time_changed",
            time: this.countUp(this.props.startTime)
          });
        }
      }
    });

    // Start timer
    this.state.timer.start();
  };

  //-----------------------------------------------------------------------
  // Render
  //-----------------------------------------------------------------------
  render() {
    return (
      <footer className="menu_component" id="menu_component">
        <Container
          className={classNames({
            mobile: is.inArray(this.state.breakpoint, ["xs"])
          })}
          id="menu_container"
        >
          <Row>
            <Col>
              <If condition={!this.props.shouldShowBackButton && !this.props.isPuzzlePage}>
                {this.homeButtonElement()}
                {this.helpButtonElement()}
              </If>
              <If condition={this.props.shouldShowBackButton || this.props.isPuzzlePage || !isGamedistributionApp()}>
                <div className="left_menu_container" key="left_menu">
                  <If condition={this.props.shouldShowBackButton}>
                    {this.backButtonElement()}
                  </If>
                  <If condition={this.props.isPuzzlePage}>
                    {this.settingsButtonElement()}
                    {this.helpButtonElement()}
                  </If>
                  <If condition={!isGamedistributionApp() && !this.props.shouldShowBackButton}>
                    <Button
                      className={classNames("solitaire_button")}
                      key="solitaire"
                      tag={LinkComponent}
                      to="https://online-solitaire.com/"
                      title="Solitaire"
                    >
                      <span>Play Solitaire</span>
                    </Button>
                  </If>
                  <If condition={!isGamedistributionApp() && !this.props.shouldShowBackButton}>
                    <Button
                      className={classNames("worldofcardgames_button")}
                      key="worldofcardgameshearts"
                      tag={LinkComponent}
                      to="https://worldofcardgames.com/hearts"
                      title="Hearts"
                    >
                      <span>Hearts</span>
                    </Button>
                  </If>
                  <If condition={!isGamedistributionApp() && !this.props.shouldShowBackButton}>
                    <Button
                      className={classNames("worldofcardgames_button")}
                      key="worldofcardgamesspades"
                      tag={LinkComponent}
                      to="https://worldofcardgames.com/spades"
                      title="Spades"
                    >
                      <span>Spades</span>
                    </Button>
                  </If>
                  <If condition={!isGamedistributionApp() && !this.props.shouldShowBackButton}>
                    <Button
                      className={classNames("worldofcardgames_button")}
                      key="worldofcardgameseuchre"
                      tag={LinkComponent}
                      to="https://worldofcardgames.com/euchre"
                      title="Euchre"
                    >
                      <span>Euchre</span>
                    </Button>
                  </If>
                  <Button
                    className={classNames("worldofcardgames_button")}
                    key="papergames"
                    tag={LinkComponent}
                    to="https://papergames.io/"
                    title="Boardgames"
                  >
                    <span>Boardgames</span>
                  </Button>
                </div>
              </If>
              <Choose>
                <When condition={this.props.isPuzzlePage}>
                  {this.shuffleButtonElement()}
                  {this.previewButtonElement()}
                  {this.edgesButtonElement()}
                  {this.scoreContainerElement()}
                </When>
              </Choose>
              {this.settingsModalElement()}
              {this.helpModalElement()}
            </Col>
          </Row>
        </Container>
        <WonModalComponent
          onRef={ref => {
            this.wonModalComponent = ref;
          }}
          startTime={this.props.startTime}
        />
      </footer>
    );
  }
}

export default withStyles(styles)(MenuComponent);



// WEBPACK FOOTER //
// ./src/components/MenuComponent/index.js